import type { CSSProperties } from 'react';

import type { PriceBookContent } from '@/blocks/domain/types';
import { isPriceBookNumericColumn } from '@/blocks/domain/helpers/priceBookColumnKeys';
import { BORDER_COLORS, TEXT_COLORS } from '@/common/presentation/constants/colors';
import { getViewerLocale } from '@/viewer-locale/application/viewerLocaleStore';
import type { RendererProps } from '../../domain/RendererContract';
import { buildTableColumnWidthPercentages } from '../../domain/buildTableColumnWidthPercentages';
import { resolveContextCurrency, resolveContextCultureTag } from '../../domain/formatDocumentMoneyCell';
import { selectPriceBookLayout } from '../../domain/priceBook/selectPriceBookLayout';
import { measuredFontSizeCss } from '@/document-render/application/blockFontSize';
import { MONEY_CELL_STYLE, MONEY_COLUMN_MIN_WIDTH } from './moneyCellStyles';
import { PriceBookBySheet } from './priceBook/PriceBookBySheet';
import { PriceBookFlatTable } from './priceBook/PriceBookFlatTable';
import { PriceBookSectionsShape } from './priceBook/PriceBookSectionsShape';

function resolveHeaderFontSize(isCompact: boolean, measured: string | undefined): number | string {
  if (measured) return measured;
  return isCompact ? 9 : 10;
}

function buildHeaderCellStyle(isCompact: boolean, fontSize: number | string): CSSProperties {
  return {
    padding: isCompact ? '6px 8px' : '8px 10px',
    fontSize,
    letterSpacing: isCompact ? 'normal' : '.08em',
    textTransform: isCompact ? 'none' : 'uppercase',
    fontWeight: 700,
    color: TEXT_COLORS.ON_FAINT,
    whiteSpace: isCompact ? 'nowrap' : undefined,
    overflowWrap: 'break-word',
    wordBreak: 'normal'
  };
}

function resolveHeaderMinWidth(isMoney: boolean, isCompact: boolean, widthPercent: number | undefined): string | number | undefined {
  if (isMoney) return MONEY_COLUMN_MIN_WIDTH;
  if (isCompact && widthPercent !== undefined) return `${widthPercent}%`;
  return undefined;
}

export function Header(props: RendererProps<PriceBookContent>) {
  const { content, viewport } = props;
  const isCompact = viewport?.compact === true;
  const cols = [...(content.columns ?? [])].filter((c) => c.visible).sort((a, b) => a.order - b.order);
  const columnWidths = buildTableColumnWidthPercentages(
    cols.map((col) => col.key),
    isPriceBookNumericColumn
  );
  const headerFontSize = resolveHeaderFontSize(isCompact, measuredFontSizeCss(props.style));
  const headerCellStyle = buildHeaderCellStyle(isCompact, headerFontSize);

  return (
    <thead>
      <tr style={{ background: BORDER_COLORS.FAINT }}>
        {cols.map((col, index) => {
          const isMoney = isPriceBookNumericColumn(col.key);
          return (
            <th
              key={col.key}
              style={{
                ...headerCellStyle,
                width: isCompact ? undefined : `${columnWidths[index]}%`,
                minWidth: resolveHeaderMinWidth(isMoney, isCompact, columnWidths[index]),
                textAlign: isMoney ? 'right' : 'left',
                ...(isMoney ? MONEY_CELL_STYLE : {})
              }}
            >
              {col.label}
            </th>
          );
        })}
      </tr>
    </thead>
  );
}

export function Component(props: RendererProps<PriceBookContent>) {
  const { content, context } = props;
  const viewerLocale = getViewerLocale();
  const currency = resolveContextCurrency(context as Record<string, unknown>, viewerLocale.currency);
  const cultureTag = resolveContextCultureTag(context as Record<string, unknown>, viewerLocale.cultureTag);
  const rows = Array.isArray(context.priceBook) ? context.priceBook : [];
  const layout = selectPriceBookLayout(rows, context.previewValueBinding);
  if (layout === 'by-sheet') {
    return (
      <PriceBookBySheet
        rows={rows}
        currency={currency}
        cultureTag={cultureTag}
        title={content.title}
        style={props.style}
        slice={props.slice}
      />
    );
  }
  if (layout === 'sections') return <PriceBookSectionsShape content={content} style={props.style} />;
  return <PriceBookFlatTable {...props} currency={currency} cultureTag={cultureTag} header={<Header {...props} />} />;
}

export const renderer = {
  Component,
  flowBehavior: 'splittable' as const,
  Header
};

export type { PriceBookContent };
